import { type FC, useMemo } from 'react';

// MUI
import { Paper, Box } from '@mui/material';

// screen
import { TemplateSettings } from 'modules/map/components/daily-dish/types';
import { ClientModel, MMapNutritionMenuModel } from 'modules/map/types';
import { formatLocalDate } from 'modules/map/components/daily-dish/utils';
import TemplateHeader from 'modules/map/components/daily-dish/templates/components/TemplateHeader';
import { LayoutId } from 'modules/map/components/daily-dish/constants';
import { MENU_LIMITS } from 'modules/map/components/daily-dish/config';

// layouts
import TwoColumnsLayout from 'modules/map/components/daily-dish/templates/components/layouts/TwoColumnsLayout';
import SixColumnsLayout from 'modules/map/components/daily-dish/templates/components/layouts/SixColumnsLayout';
import TwoRowsLayout from 'modules/map/components/daily-dish/templates/components/layouts/TwoRowsLayout';

interface NewTemplateProps {
  clientData: ClientModel;
  data: MMapNutritionMenuModel;
  settings: TemplateSettings;
  localBackgroundUrl?: string | null;
}

const NewTemplate: FC<NewTemplateProps> = ({ clientData, data, settings, localBackgroundUrl }) => {
  const { structuredData } = data;

  const backgroundImage = useMemo(() => {
    if (settings.general.localBackground && localBackgroundUrl) {
      return localBackgroundUrl;
    }
    const file = settings.general.backgroundFile;
    return file ? `/menu-background_image/${file}` : undefined;
  }, [settings.general.backgroundFile, settings.general.localBackground, localBackgroundUrl]);

  const fromDate = formatLocalDate(structuredData.metadata.fromDate);
  const toDate = formatLocalDate(structuredData.metadata.toDate);

  const days = structuredData.data.items;

  const maxCount = days.reduce(
    (acc, next) => {
      return {
        lunch: Math.max(acc.lunch, next.dishes.length, MENU_LIMITS.dishLimit) as number,
        afternoon: Math.max(
          acc.afternoon,
          next.afternoons.length,
          MENU_LIMITS.afternoonLimit
        ) as number,
      };
    },
    {
      lunch: MENU_LIMITS.dishLimit as number,
      afternoon: MENU_LIMITS.afternoonLimit as number,
    }
  );

  const getDishes = <T extends string[] | string[][]>(
    dishes: T = [] as unknown as T,
    limit: number
  ): T => {
    const result = [...dishes, ...Array(limit - dishes.length).fill(null)];
    if (result.every((item) => item === null)) {
      return [] as unknown as T;
    }
    return result.slice(0, limit) as T;
  };

  const isInvalidData = useMemo(() => days.every((day) => !day.dishes.length), [days]);

  if (isInvalidData) return null;

  // Render the appropriate layout based on layoutId
  const renderLayout = () => {
    const layoutId = settings.general.layoutId;
    const layoutProps = { days, settings, maxCount, getDishes };

    switch (layoutId) {
      case LayoutId.TWO_COLUMNS_LEFT:
        return <TwoColumnsLayout {...layoutProps} />;
      case LayoutId.SIX_COLUMNS:
        return <SixColumnsLayout {...layoutProps} />;
      case LayoutId.TWO_ROWS:
        return <TwoRowsLayout {...layoutProps} />;
      default:
        return <TwoColumnsLayout {...layoutProps} />;
    }
  };

  return (
    <Paper
      sx={{
        bgcolor: 'inherit',
        position: 'relative',
        backgroundSize: 'cover',
        backgroundImage: backgroundImage ? `url("${backgroundImage}")` : undefined,
        width: '1920px',
        height: '1080px',
        overflow: 'hidden',
      }}
    >
      <TemplateHeader
        clientData={clientData}
        settings={settings}
        weekTitle={structuredData.data.info.weekTitle}
        fromDate={fromDate}
        toDate={toDate}
      />
      <Box
        sx={{
          width: '100%',
          height: 'calc(100% - 140px)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'hidden',
          p: 1,
        }}
      >
        {renderLayout()}
      </Box>
    </Paper>
  );
};

export default NewTemplate;
